import { createDeepAAPI } from '@deepcloud/api-deep-a';
import AuthService from '@/services/authService.ts';
import { createGlobalState } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { computed, ref, shallowRef } from 'vue';
import { useKeycloak } from '@/composables/useKeycloak.ts';
import { createDeepAdminAPI } from '@deepcloud/api-deep-admin';
import { createDeepAdminBackendAPI } from '@deepcloud/api-deep-admin-backend';
import { createDeepSignAPIV1 } from '@deepcloud/api-deep-sign';
import { AxiosHttpClient } from '@deepcloud/api-http-client/axios';
import { createDeepBoxAPIV1 } from '@deepcloud/api-deep-box';
import { createDeepValidatorAPI } from '@deepcloud/api-deep-validator';
import { applyDeepSignInterceptors } from '@/api/deepsign/applyDeepSignInterceptors.ts';
import axios, { type AxiosInstance } from 'axios';
import { onRejected } from '@/api/interceptor';
import { setAuthorizationHeaders } from '@/api/utils.ts';

const AUTH_ERROR_INTERCEPTORS_APPLIED = '__authErrorInterceptorsApplied';

type MarkedAxiosInstance = AxiosInstance & {
  [AUTH_ERROR_INTERCEPTORS_APPLIED]?: boolean;
};

function getAxiosInstance(httpClient: unknown): AxiosInstance {
  return (httpClient as { instance: AxiosInstance }).instance;
}

function applyAuthAndErrorInterceptors(axiosInstance: AxiosInstance) {
  const instance = axiosInstance as MarkedAxiosInstance;

  // Prevent double-registration in case the same axios instance is reused.
  if (instance[AUTH_ERROR_INTERCEPTORS_APPLIED]) return;
  instance[AUTH_ERROR_INTERCEPTORS_APPLIED] = true;

  instance.interceptors.request.use(async (config) => {
    const headers: Record<string, string> = {};

    await setAuthorizationHeaders(headers);

    config.headers = config.headers ?? {};
    Object.assign(config.headers, headers);

    return config;
  });

  instance.interceptors.response.use((response) => response, onRejected);
}

export const useApi = createGlobalState(() => {
  const ADAPTER_TYPE = 'axios';
  const { locale } = useI18n();

  const { addListener } = useKeycloak();
  const token = ref(AuthService.fullAccessToken);

  addListener('onAuthRefreshSuccess', () => {
    token.value = AuthService.fullAccessToken;
  });
  const deepSignAxios = axios.create();
  applyDeepSignInterceptors(deepSignAxios);

  const deepSignApiRef = shallowRef(
    createDeepSignAPIV1({
      httpClient: new AxiosHttpClient(deepSignAxios),
      baseUrl: import.meta.env.VITE_DEEPSIGN_API_BASE_URL,
      token: () => token.value,
      lang: () => locale.value
    })
  );

  const deepSignAPI = computed(() => deepSignApiRef.value);

  const deepAAPI = computed(() => {
    const api = createDeepAAPI({
      baseUrl: import.meta.env.VITE_DEEPA_API_BASE_URL,
      adapter: ADAPTER_TYPE,
      token: () => token.value,
      lang: () => locale.value
    });

    applyAuthAndErrorInterceptors(getAxiosInstance(api.deepAHttpClient));

    return api;
  });

  // Old python DeepAdmin API
  const deepAdminAPI = computed(() => {
    const api = createDeepAdminAPI({
      baseUrl: import.meta.env.VITE_DEEPADMIN_API_BASE_URL,
      adapter: ADAPTER_TYPE,
      token: () => token.value,
      lang: () => locale.value
    });

    applyAuthAndErrorInterceptors(getAxiosInstance(api.deepAdminHttpClient));

    return api;
  });

  // New java DeepAdmin API (V1 / V2)
  const deepAdminBackendAPI = computed(() => {
    const api = createDeepAdminBackendAPI({
      baseUrl: import.meta.env.VITE_DEEPADMIN_BACKEND_API_BASE_URL,
      adapter: ADAPTER_TYPE,
      token: () => token.value,
      lang: () => locale.value
    });

    applyAuthAndErrorInterceptors(
      getAxiosInstance(api.deepAdminBackendHttpClient)
    );

    return api;
  });

  const deepBoxAPI = computed(() => {
    return createDeepBoxAPIV1({
      baseUrl: import.meta.env.VITE_DEEPBOX_API_BASE_URL,
      adapter: ADAPTER_TYPE,
      token: () => token.value,
      lang: () => locale.value
    });
  });

  // Deliberately without the shared auth/error interceptors: their 401/403/404
  // handling navigates to the error page, while DeepValidator failures are
  // reported in place by the caller (see useDeepValidator).
  const deepValidatorAPI = computed(() => {
    return createDeepValidatorAPI({
      baseUrl: import.meta.env.VITE_DEEPVALIDATOR_API_BASE_URL,
      adapter: ADAPTER_TYPE,
      token: () => token.value,
      lang: () => locale.value
    });
  });

  return {
    deepSignAPI,
    deepAAPI,
    deepAdminAPI,
    deepAdminBackendAPI,
    deepBoxAPI,
    deepValidatorAPI
  };
});
